"use client";

import cn from "classnames";
import { FC, useState, useMemo, ReactNode } from "react";
import { calcSubnets, getNetworkMask } from "./SubnetCalculator.helpers";
import { IpAddressInput } from "./IpAddressInput";
import { MaskBitsInput } from "./MaskBitsInput";
import { Output } from "./Output";
import { SubnetBitsInput } from "./SubnetBitsInput";
import { NumberOfSubnetsInput } from "./NumberOfSubnetsInput";
import { AllocationTable } from "./AllocationTable";
import { SubnetsTable } from "./SubnetsTable";
import { getDataAutomationId } from "@/helpers";

export interface SubnetCalculatorProps {
  headline?: ReactNode;
  addressBlockHeadline: string;
  allocationBlockHeadline: string;
  subnetsTableHeadline: string;
  className?: string;
}

export const SubnetCalculator: FC<SubnetCalculatorProps> = ({
  headline,
  addressBlockHeadline,
  allocationBlockHeadline,
  subnetsTableHeadline,
  className,
}) => {
  const [ipAddressError, setIpAddressError] = useState<string | null>(null);
  const [lastValidIpAddress, setLastValidIpAddress] =
    useState<string>("192.168.1.1");
  const [maskBits, setMaskBits] = useState<number>(24);
  const [subnetBits, setSubnetBits] = useState<number>(1);
  const [parentElement, setParentElement] = useState<HTMLElement | null>(null);

  // Calculate the total available host addresses per subnet, considering the network and broadcast addresses
  const totalHostAddressesPerSubnet =
    Math.pow(2, 32 - (maskBits + subnetBits + 1) + 1) - 2;

  // Used for output and calculation
  // Ensures that 'totalHostAddressesPerSubnet' is non-negative, otherwise set it to 0
  const addressesPerSubnet =
    totalHostAddressesPerSubnet > 0 ? totalHostAddressesPerSubnet : 0;

  const addresses = useMemo(() => {
    if (maskBits + subnetBits <= 32) {
      const numberOfSubnets = Math.pow(2, subnetBits);
      const networkPrefixBits = maskBits + subnetBits;

      return calcSubnets({
        ipAddress: lastValidIpAddress,
        maskAddress: getNetworkMask(maskBits),
        networkPrefixBits: networkPrefixBits,
        subnetCount: numberOfSubnets,
        addressCount: addressesPerSubnet,
      });
    }
    return [];
  }, [maskBits, subnetBits, lastValidIpAddress, addressesPerSubnet]);

  const handleMaskBitsChange = (value: number) => {
    setMaskBits(value);
  };

  const handleSubnetBitsChange = (value: number) => {
    setSubnetBits(value);
  };

  const handleLastValidIpAddressChange = (value: string) => {
    setLastValidIpAddress(value);
  };

  const handleIpAddressErrorChange = (value: string | null) => {
    setIpAddressError(value);
  };

  return (
    <section
      data-theme="gray"
      className={cn("bg-background", className)}
      ref={setParentElement}
      id="subnet-calculator"
    >
      <div className="swdc-container py-16">
        {headline && (
          <div className="flex flex-row items-center gap-4 md:justify-center">
            {headline}
          </div>
        )}
        <div className="mt-8 flex flex-row items-center gap-7.5 md:mt-10">
          <p className="text-subhead-2xs sm:text-subhead-xs text-swdc-black-600 font-bold">
            {addressBlockHeadline}
          </p>
          {ipAddressError && (
            <span
              className="text-body-2xs text-red-500"
              {...getDataAutomationId("ip-error-message")}
            >
              {ipAddressError}
            </span>
          )}
        </div>
        <div className="mt-5 flex w-full flex-col gap-4 md:flex-row md:gap-8">
          <IpAddressInput
            setLastValidIpAddress={handleLastValidIpAddressChange}
            setIpAddressError={handleIpAddressErrorChange}
          />
          <MaskBitsInput
            maskBits={maskBits}
            setMaskBits={handleMaskBitsChange}
            subnetBits={subnetBits}
          />
          <Output
            label="Network Mask"
            id="network-mask"
            htmlFor="mask-bits"
            value={maskBits ? getNetworkMask(maskBits) : "0.0.0.0"}
          />
        </div>
        <p className="text-subhead-2xs sm:text-subhead-xs text-swdc-black-600 mt-8 block font-bold">
          {allocationBlockHeadline}
        </p>
        <div className="mt-5 flex w-full flex-col gap-4 md:flex-row md:gap-8">
          <SubnetBitsInput
            subnetBits={subnetBits}
            setSubnetBits={handleSubnetBitsChange}
            maskBits={maskBits}
          />
          <NumberOfSubnetsInput
            subnetBits={subnetBits}
            setSubnetBits={handleSubnetBitsChange}
            maskBits={maskBits}
          />
          <Output
            label="Addresses per subnet"
            id="addresses-per-subnet"
            htmlFor="subnet-bits number-of-subnets"
            value={addressesPerSubnet}
          />
        </div>
        <AllocationTable
          ipAddress={lastValidIpAddress}
          maskBits={maskBits}
          subnetBits={subnetBits}
        />
        <SubnetsTable
          headline={subnetsTableHeadline}
          addresses={addresses}
          parentElement={parentElement}
        />
      </div>
    </section>
  );
};
